// @owners { team: patients-team }
import { union } from 'lodash';
import { useEffect } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getEditShipmentID } from '~shared/features/checkout/selectors/getCart';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { type Address } from '~shared/types';
import { useQueryFulfillmentOptions } from '../../queries/queries';
import { type CheckoutDateItem } from './useSelectDateCard';

type Item = {
  type?: string;
  date: string | null | undefined;
  feeLabel: string | null | undefined;
  feeOriginalAmount?: string;
  tag: string | null | undefined;
};

export const ShadowFulfillmentOptions = ({
  address,
  checkoutDateItems,
}: {
  address: Address;
  checkoutDateItems: CheckoutDateItem[];
}) => {
  const { trackPageView } = useAnalytics();
  const { prescriptionIDs } = usePrescriptionsInCart();
  const shipmentID = useSelectorShared(getEditShipmentID);
  const { isSuccess, courierDateFulfillmentOptions, courierAnyDateFulfillmentOption } = useQueryFulfillmentOptions({
    addressID: address.id,
    cartPrescriptionIDs: prescriptionIDs,
    shipmentID,
  });

  useEffect(() => {
    if (!isSuccess) return;
    if (checkoutDateItems.length === 0) return;

    const computedByClient: Item[] = checkoutDateItems.map((item) => ({
      type: item.type,
      date: item.date,
      feeLabel: item.feeAmountLabel,
      feeOriginalAmount: item.feeOriginalAmountLabel,
      tag: item.tag,
    }));

    const computedByServer: Item[] = [];

    if (courierDateFulfillmentOptions) {
      courierDateFulfillmentOptions.forEach((item) => {
        const fee = item.available_fees?.[0];
        computedByServer.push({
          type: fee?.fee_type,
          date: item.date,
          feeLabel: item.fee_amount_label,
          feeOriginalAmount: fee ? '$' + fee.original_amount : undefined,
          tag: item.tag,
        });
      });
    }

    // if the any-date option has a fee (e.g. cost plus) include it in the server-computed options
    if (courierAnyDateFulfillmentOption?.available_fees?.length) {
      const item = courierAnyDateFulfillmentOption;
      const fee = item.available_fees?.[0];
      computedByServer.push({
        type: fee?.fee_type,
        date: item.date,
        feeLabel: item.fee_amount_label,
        feeOriginalAmount: fee ? '$' + fee.original_amount : undefined,
        tag: item.tag,
      });
    }

    trackPageView({
      event: EVENTS.CHECKOUT__SHADOW_FULFILLMENT_OPTIONS,
      params: {
        computedByClient,
        computedByServer,
        optionsMatch: validateOptions(computedByClient, computedByServer),
        origin: shipmentID ? 'edit delivery' : 'checkout',
      },
    });
  }, [
    checkoutDateItems,
    courierAnyDateFulfillmentOption,
    courierDateFulfillmentOptions,
    isSuccess,
    shipmentID,
    trackPageView,
  ]);

  return null;
};

const validateOptions = (client: Item[], server: Item[]) => {
  const dates = union(
    client.map((item) => item.date),
    server.map((item) => item.date),
  );

  return dates.every((date) => {
    const clientItem = client.find((item) => item.date === date);
    const serverItem = server.find((item) => item.date === date);

    // validate that there is both a client-computed and server-computed option for each date
    if (!clientItem || !serverItem) return false;

    // validate that the fees match
    if (clientItem.feeLabel !== serverItem.feeLabel) return false;

    // validate that the types match
    switch (clientItem.type) {
      case 'any-day-delivery':
        // if the any-day option is free there should be no fee type returned by the server
        // if the any-day option is not free (e.g. cost_plus) the fee type should be 'standard_delivery'
        return !serverItem.type || serverItem.type === 'standard_delivery';
      case 'order-bundling':
        // if the client type is order bundling, the server should have a tag of 'BUNDLED'
        return serverItem.tag === 'BUNDLED';
      default:
        // otherwise the types should match, e.g. same-day-delivery -> same_day_delivery
        return clientItem.type === serverItem.type?.replace(/_/g, '-');
    }
  });
};
